import 'firebase/compat/auth'
import 'firebase/compat/firestore'

import { ActionContext, Module } from 'vuex'
import { AuthState, RootState } from './state_models'
import { getDecodedJWT, isJwtTokenExpired, setJWT } from '../util/jwt_helper'
import { handleMarketplaceRedirect, isMarketplaceRedirect, setMarketplaceCookie } from '../util/marketplace_helper'

import { AuthUser } from '../models'
import { IAuthToken } from '../models/auth_user'
import { OauthService, UnauthenticatedService } from '../services'
import firebase from 'firebase/compat/app'
import localStorage from 'store'
import moment from 'moment'
import restAgentV1 from '../restAgent'
import { RouterRegistry } from '../util/routerRegistry'
import { destroyFreshchatWidget } from '../util/freshchat'
import { useEmitter } from '../pmd/composition/useEmitter'
import { clearPendoSession } from '../util/pendo'
import { localizationKey } from '@/localization'

export const auth: Module<AuthState, RootState> = {
  namespaced: true,
  state: {
    user: undefined,
    locationId: undefined as undefined | string,
    isUserSwitching: false,
    isTokenRefreshing: false,
    inactivityLogout: false,
    isLoggingOut: false,
  } as AuthState,
  getters: {
    getAuth: state => {
      return state.user
    },
    isProxyUser: state => {
      if (!state?.user?.authToken) {
        return false
      }
      const jwt = getDecodedJWT(state?.user?.authToken || '') as IAuthToken
      return jwt.primaryAuthClassId != jwt.authClassId
    },
    isJwtExist: state => {
      return !!state.user?.authToken && !!state.user?.refreshToken
    },
    isUserSwitching: state => {
      return state.isUserSwitching
    },
    isTokenRefreshing: state => {
      return state.isTokenRefreshing
    },
    isInactivityLogout: state => {
      return state.inactivityLogout
    },
    isSupportAgent: state => {
      if (!state?.user?.authToken) return false
      const jwt = getDecodedJWT(state?.user?.authToken || '') as IAuthToken
      return !!jwt?.appMeta?.supportAgentId
    },
  },
  mutations: {
    setLocationId: (state: AuthState, locationId: string) => {
      state.locationId = locationId
    },
    set: (state: AuthState, payload: AuthUser) => {
      state.user = payload
    },
    delete: (state: AuthState) => {
      state.user = undefined
    },
    setUserSwitching: (state: AuthState, isSwitching: boolean) => {
      state.isUserSwitching = isSwitching
    },
    setTokenRefreshing: (state: AuthState, isRefreshing: boolean) => {
      state.isTokenRefreshing = isRefreshing
    },
    updateInactivityLogout: (state: AuthState, inactivityLogout: boolean) => {
      state.inactivityLogout = inactivityLogout
    },
    setLoggingOut: (state: AuthState, isLoggingOut: boolean) => {
      (state as any).isLoggingOut = isLoggingOut
    },
  },
  actions: {
    refreshFirebaseToken: async (context: ActionContext<AuthState, RootState>, params: { [key: string]: any }) => {
      //
      if (
        !params.locationId ||
        (context.state.locationId && params.locationId === context.state.locationId && !params.refresh)
      ) {
        return
      }

      context.commit('setLocationId', params.locationId)
      const { activeLocations, isChanged } = await context.dispatch(
        'locations/addToActiveLocation',
        params.locationId && typeof params.locationId === 'string' ? [params.locationId] : params.locationId,
        { root: true }
      )
      if (!params.refresh && !isChanged) {
        return
      }

      // To capture initial token state
      let oldFirebaseToken = ''
      let locationsBefore: string[] = []

      try {
        const firebaseToken = (await firebase?.auth()?.currentUser?.getIdToken()) as string
        const decodedPayload = getDecodedJWT(firebaseToken)
        // Capture initial token state
        locationsBefore = decodedPayload?.locations || []
        oldFirebaseToken = firebaseToken
        if (decodedPayload?.locations?.includes(params.locationId)) {
          console.log('skipping refresh token api call [reason: duplicate]')
          return
        }
      } catch (e) {
        console.error(e)
      }

      try {
        const currentRequestTimestamp = Date.now()
        const response = await OauthService.signinRefresh(activeLocations)
        // storing token refresh timestamp to handle race condition
        // older request should never overwrite the current token
        const lastRefreshTimestamp = localStorage.get('tokenRefreshTimestamp')
        const isInvalidRequest = lastRefreshTimestamp && lastRefreshTimestamp > currentRequestTimestamp
        if (response && !isInvalidRequest) {
          localStorage.set('tokenRefreshTimestamp', currentRequestTimestamp)
          await firebase.auth().signInWithCustomToken(response.token)
          localStorage.set('refreshedToken', response.token)
          restAgentV1.setApiKey(response.token)
        }
      } catch (e) {
        console.error(`Error in refreshing firebase token : ${JSON.stringify(e)}`)
      }
    },
    get: (context: ActionContext<AuthState, RootState>) => {
      return new Promise((resolve, reject) => {
        if (context.state.user) {
          return resolve(context.state.user)
        }
        const b64String = localStorage.get('a')
        if (!b64String) return reject('The user is not logged in. user_not_logged_in')

        const user = JSON.parse(atob(b64String))
        if (user?.authToken && !isJwtTokenExpired(user.authToken)) {
          setMarketplaceCookie(user.authToken)
        }

        if (window.location.search) {
          const urlObj = new URL(window.location.href)
          const redirectParam = urlObj.searchParams.get('redirect')
          if (redirectParam && user?.authToken && !isJwtTokenExpired(user.authToken)) {
            handleMarketplaceRedirect(redirectParam)
          }
        }
        context.commit('set', user)
        setJWT(user?.jwt, user?.authToken)
        return resolve(user)
      })
    },
    setTokenRefreshing: (context: ActionContext<AuthState, RootState>, isTokenRefreshing: boolean) => {
      context.commit('setTokenRefreshing', isTokenRefreshing)
    },
    set: (context: ActionContext<AuthState, RootState>, payload: AuthUser) => {
      context.commit('set', payload)
      localStorage.set('a', btoa(JSON.stringify(payload)))
      localStorage.set('loginDate', moment().toDate())
      if ((window as any).isElectron) {
        ;(window as any).electronAPI.setAuthenticated()
      }

      const hostName = window.location.hostname.split('.')
      const hostLength = hostName.length
      let domainName = 'localhost'
      if (hostName[0] !== 'localhost') {
        domainName = `.${hostName[hostLength - 2]}.${hostName[hostLength - 1]}`
        if (hostName.includes('gohighlevel') || hostName.includes('leadconnectorhq')) {
          const reducedPayload = (({ apiKey, userId, companyId }) => ({ apiKey, userId, companyId }))(payload)
          document.cookie = `a=${btoa(
            JSON.stringify(reducedPayload)
          )};domain=${domainName};path=/;max-Age=31536000;secure;`
        }
        setMarketplaceCookie(payload?.authToken)

        if (window.location.search.includes('redirect=')) {
          const redirectParam = new URLSearchParams(window.location.search).get('redirect')
          if (redirectParam && payload?.authToken) {
            handleMarketplaceRedirect(redirectParam)
          }
        }
      }
      setJWT(payload?.jwt, payload?.authToken)
    },
    switchToMasterUser: {
      handler: async (context: ActionContext<AuthState, RootState>) => {
        const router = RouterRegistry.getRouter()
        try {
          context.commit('setUserSwitching', true)
          context.commit('setAgencyLoaderState', true, { root: true })
          const authUser = await context.dispatch('auth/get', null, { root: true })
          const signedInUserData = authUser

          setJWT(signedInUserData.jwt, signedInUserData.authToken, true)
          const data = await OauthService.getCurrentUser()
          const authSetData = {
            firebaseToken: data.token,
            jwt: data.jwt,
            refreshJwt: data.refreshJwt,
            apiKey: data.apiKey,
            userId: data.userId,
            companyId: data.companyId,
            authToken: data.authToken,
            refreshToken: data.refreshToken,
          }

          /* Log out User*/
          await context.dispatch('logoutForUserSwitch', null, { root: true })
          await firebase.auth().signOut()

          /* switch back to logged user*/
          await context.dispatch('auth/set', authSetData, { root: true })
          await firebase.auth().signInWithCustomToken(authSetData.firebaseToken)
          await context.dispatch('auth/set', authSetData, { root: true })

          await context.dispatch('init', null, { root: true })
          await context.dispatch('user/get', null, { root: true })
          await context.dispatch('company/get', null, { root: true })

          await context.dispatch(
            'auth/refreshFirebaseToken',
            { locationId: router.currentRoute?.value?.params?.location_id || null, refresh: true },
            { root: true }
          )
          localStorage.set('proxyLoginCount', (localStorage.get('proxyLoginCount') || 0) + 1)
          window.location.reload()
        } catch (error) {
          console.error('something went wrong while switching to master user', error)
          window.location.reload()
        } finally {
          context.commit('setAgencyLoaderState', false, { root: true })
        }
      },
    },
    logout: {
      root: true,
      handler: async (context: ActionContext<AuthState, RootState>) => {
        // Prevent multiple simultaneous logout calls
        if ((context.state as any).isLoggingOut) {
          console.log('Logout already in progress, skipping')
          return
        }

        context.commit('setLoggingOut', true)

        // Signout from backend
        try {
          const a = localStorage.get('a') ? JSON.parse(atob(localStorage.get('a'))) : null
          const apiKey = context.state.user?.apiKey || a?.apiKey
          const refreshTokenV1 = context.state.user?.refreshJwt || a?.refreshJwt
          const refreshTokenV2 = context.state.user?.refreshToken || a?.refreshToken
          // Do not await this call, it will block the main thread
          if (apiKey || refreshTokenV1 || refreshTokenV2) {
            UnauthenticatedService.signout(apiKey, refreshTokenV1, refreshTokenV2)
          }
        } catch (error) {
          console.error('Logout from backend failed:', error)
        }

        // Signout from firebase
        try {
          // Check if there's an active session before signing out
          if (firebase.auth().currentUser) {
            await firebase.auth().signOut()
          }
        } catch (error) {
          console.error('Logout from firebase failed:', error)
        }

        // Terminate Firestore connections and attempt to clear persistence
        try {
          // Only if Firestore is loaded (compat)
          if (firebase.firestore) {
            await firebase.firestore().terminate()
            // clearPersistence may fail if a Firestore instance was already active; ignore in that case
            await firebase
              .firestore()
              .clearPersistence()
              .catch(() => {})
          }
        } catch (e) {
          console.error('Failed to terminate/clear Firestore:', e)
        }

        // Unregister Firebase Messaging service workers (if any)
        try {
          if (navigator?.serviceWorker?.getRegistrations) {
            const regs = await navigator.serviceWorker.getRegistrations()
            await Promise.all(
              regs
                .filter(r => {
                  const url = r?.active?.scriptURL || r?.installing?.scriptURL || ''
                  return url.includes('firebase-messaging-sw') || url.includes('messaging-sw')
                })
                .map(r => r.unregister())
            )
          }
        } catch (e) {
          console.error('Failed to unregister messaging service workers:', e)
        }
        const lanuageChosen = window.localStorage.getItem(localizationKey)
        // Clear local storage
        window.localStorage.clear()

        // Clear session storage
        window.sessionStorage.clear()
        if (lanuageChosen) {
          window.localStorage.setItem(localizationKey, lanuageChosen)
        }

        if ((window as any).isElectron) {
          ;(window as any).electronAPI.logout()
        }

        // Clear cookies
        document.cookie.split(";").forEach(function (c) { document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); });
        document.cookie = `a=; Max-Age=-99999999; domain=.${window.location.hostname.split('.').slice(-2).join('.')}; path=/;` // remove the a cookie for the current domain
        document.cookie = 'a=; Max-Age=-99999999;domain=.gohighlevel.com;path=/;' // remove the a cookie for the gohighlevel domain
        document.cookie = 'a=; Max-Age=-99999999; domain=.leadconnectorhq.com; path=/;' // remove the a cookie for the leadconnectorhq domain

        // Clear pendo
        // Sources:
        //  1. https://support.pendo.io/hc/en-us/articles/17541315915035-Data-collection-prevention-strategies
        //  2. https://agent.pendo.io/public/identity/
        clearPendoSession()

        // Clear google accounts
        if (window.google && window.google?.accounts) {
          window.google.accounts.id?.disableAutoSelect()
        }

        // Destroy freshchat widget
        try {
          destroyFreshchatWidget()
        } catch (error) {
          console.error('Failed to destroy freshchat widget:', error)
        }

        // Emit event to hide launchpad
        const emitter = useEmitter()
        emitter.emit('launchpad-highly-visibility-update', { isVisible: false })
        emitter.emit('common-conditional-components-logout', true)

        // Clear Vuex store
        context.commit('delete')

        // Remove loading class from app
        document.getElementById('app')?.classList.remove('loading')
        context.commit('setLoggingOut', false)
      },
    },
    logoutForUserSwitch: {
      root: true,
      handler: async (context: ActionContext<AuthState, RootState>) => {
        localStorage.remove('app_referer')
        context.commit('delete')
        localStorage.remove('a')
        localStorage.remove('loginDate')
        if (window.pendo) {
          window.pendo.clearSession()
        }

        document.cookie = 'a=; Max-Age=-99999999;domain=.gohighlevel.com;path=/;'

        document.cookie = 'a=; Max-Age=-99999999; domain=.leadconnectorhq.com; path=/;'

        if (window.google && window.google?.accounts) {
          window.google?.accounts?.id?.disableAutoSelect()
        }

        window.localStorage.clear()
        window.sessionStorage.clear()
        document.cookie.split(';').forEach(function (c) {
          document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/')
        })
      },
    },
    changeUserSwitching(context: ActionContext<AuthState, RootState>, isSwitching) {
      context.commit('setUserSwitching', isSwitching)
    },
    forceInactiveLogout: {
      handler: (context: ActionContext<AuthState, RootState>, inactiveLogout) => {
        context.commit('updateInactivityLogout', inactiveLogout)
      },
    },
  },
}
